import {
  Box,
  Chip,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import React, { useMemo, useState } from 'react';

import type { KubernetesConsoleApi } from './api';
import { useKubernetesWatch } from './useKubernetesWatch';
import { COLORS, EventsTable } from './KubernetesPage';
import type { K8sEvent } from './types';

type EventTypeFilter = 'all' | 'Normal' | 'Warning';

/**
 * Live, filterable Events tab for the resource detail drawer. Replaces the
 * one-shot `api.proxy` fetch with `useKubernetesWatch` on the same
 * fieldSelector-scoped events path, so new events for this object show up
 * without a manual refresh.
 */
export function EventsPanel({
  api,
  cluster,
  namespace,
  namespaced,
  name,
}: {
  api: KubernetesConsoleApi;
  cluster: string;
  namespace: string;
  namespaced: boolean;
  name: string;
}) {
  const [typeFilter, setTypeFilter] = useState<EventTypeFilter>('all');
  const [textFilter, setTextFilter] = useState('');

  // Same fieldSelector logic the previous one-shot fetch used — scoped to
  // this object (and, for namespaced kinds, this namespace).
  const eventsWatchPath = useMemo(() => {
    if (!cluster || !name) return null;
    return namespaced
      ? `/api/v1/namespaces/${encodeURIComponent(
          namespace,
        )}/events?fieldSelector=${encodeURIComponent(
          `involvedObject.name=${name},involvedObject.namespace=${namespace}`,
        )}`
      : `/api/v1/events?fieldSelector=${encodeURIComponent(
          `involvedObject.name=${name}`,
        )}`;
  }, [cluster, name, namespace, namespaced]);

  const watch = useKubernetesWatch(api, cluster, eventsWatchPath);
  // The watch is generically typed as K8sObject — these are really Events.
  const events = watch.items as unknown as K8sEvent[];

  const filtered = useMemo(() => {
    const term = textFilter.trim().toLowerCase();
    return [...events]
      .filter(
        event =>
          typeFilter === 'all' || (event.type ?? 'Normal') === typeFilter,
      )
      .filter(
        event =>
          !term ||
          (event.message ?? '').toLowerCase().includes(term) ||
          (event.reason ?? '').toLowerCase().includes(term),
      )
      .sort((a, b) =>
        (b.lastTimestamp ?? b.eventTime ?? '').localeCompare(
          a.lastTimestamp ?? a.eventTime ?? '',
        ),
      );
  }, [events, typeFilter, textFilter]);

  return (
    <Box>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
        <TextField
          select
          size="small"
          label="Type"
          value={typeFilter}
          onChange={event =>
            setTypeFilter(event.target.value as EventTypeFilter)
          }
          sx={{ minWidth: 130 }}
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="Normal">Normal</MenuItem>
          <MenuItem value="Warning">Warning</MenuItem>
        </TextField>
        <TextField
          size="small"
          placeholder="Filter by message or reason"
          value={textFilter}
          onChange={event => setTextFilter(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: COLORS.muted }} />
              </InputAdornment>
            ),
          }}
          sx={{ width: 260 }}
        />
        <Box sx={{ flex: 1 }} />
        <Tooltip
          title={
            watch.error ??
            (watch.connected
              ? 'Live Kubernetes watch connected'
              : 'Using request-based refresh')
          }
        >
          <Chip
            size="small"
            variant="outlined"
            label={
              watch.connected
                ? 'Live'
                : watch.reconnecting
                ? 'Reconnecting'
                : 'Refresh'
            }
            sx={{
              height: 20,
              fontSize: 10,
              color: watch.connected
                ? COLORS.green
                : watch.reconnecting
                ? COLORS.amber
                : COLORS.muted,
              borderColor: watch.connected
                ? `${COLORS.green}88`
                : watch.reconnecting
                ? `${COLORS.amber}88`
                : COLORS.border,
            }}
          />
        </Tooltip>
      </Stack>
      <EventsTable events={filtered} />
    </Box>
  );
}
